import React, { useEffect, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";

import Card from "../../../../components/card";
import PaginationComponent from "../../../../components/pagination/pagination";
import SearchInputComponent from "../../../../components/searchInput/searchInput";
import HttpManager from "../../../../services/HttpManager";
import { WorkshopRowMultiDTO, WorkshopTab, WorkshopTabCounts } from "../../../../services/v2/workshops.service";
import useEsTelefono from "../../../../utils/esTelefono";
import { cursoCorto } from "../../../../utils/cursoCorto";
import { buildPages } from "../enrollmentRequests/helpers";
import S from "./strings";
import { errorText, useOpciones, usePlantel } from "./comun";
import { Chip, diasTexto, monto, rangoHoras } from "./piezasTaller";
import WorkshopForm from "./workshopForm";
import TallerVentana from "./tallerVentana";

/*
	Talleres y Personalizados (Módulos Claros, 2026-11-01), with the pieces of Matrículas and Promociones: «Nuevo Taller» on top,
	everything inside one card, tabs by dates (Próximos · En Curso · Terminados · Anulados; number only on the first two, where
	there is work), the search box on the same line, a real table (cards on the phone) and the pagination. A click on a row
	opens the workshop window. A Personalizado (one person) shows the student's name.
*/

const http = HttpManager.getInstance();
const PAGE_SIZES = [50, 100];
const CON_NUMERO: WorkshopTab[] = ["proximos", "enCurso"];

const WorkshopsController: React.FC = () => {
	const movil = useEsTelefono();
	const { campusId } = usePlantel();
	const opciones = useOpciones(campusId);
	const [tab, setTab] = useState<WorkshopTab>("enCurso");
	const tocoPestana = useRef<boolean>(false);
	const [search, setSearch] = useState<string>("");
	const [buscar, setBuscar] = useState<string>("");
	const [page, setPage] = useState<number>(1);
	const [perPage, setPerPage] = useState<number>(PAGE_SIZES[0]);
	const [datos, setDatos] = useState<{ tab: WorkshopTab; rows: WorkshopRowMultiDTO[]; total: number; lastPage: number } | null>(null);
	const [counts, setCounts] = useState<WorkshopTabCounts | null>(null);
	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const [aviso, setAviso] = useState<string | null>(null);
	const [abierto, setAbierto] = useState<number | null>(null);
	const [nuevo, setNuevo] = useState<boolean>(false);
	const [refresco, setRefresco] = useState<number>(0);

	useEffect(() => { tocoPestana.current = false; setTab("enCurso"); setPage(1); setDatos(null); setCounts(null); setAbierto(null); }, [campusId]);

	useEffect(() => {
		if (campusId === null) return;
		let vigente = true;
		setLoading(true);
		http.workshopsService.listTab(campusId, { tab, q: buscar, page, perPage })
			.then((r) => {
				if (!vigente) return;
				setDatos({ tab, rows: r.data.rows || [], total: r.data.total || 0, lastPage: r.data.lastPage || 1 });
				setCounts(r.data.counts || null);
				setError(null);
			})
			.catch((e) => { if (vigente) { setError(errorText(e)); setDatos({ tab, rows: [], total: 0, lastPage: 1 }); } })
			.finally(() => { if (vigente) setLoading(false); });
		return () => { vigente = false; };
	}, [campusId, tab, buscar, page, perPage, refresco]);

	// Nothing running and no tab chosen yet: open on Próximos (and on Terminados if there is nothing upcoming either).
	useEffect(() => {
		if (tocoPestana.current || !counts || tab !== "enCurso" || counts.enCurso > 0) return;
		setTab(counts.proximos > 0 ? "proximos" : counts.terminados > 0 ? "terminados" : "enCurso");
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [counts]);

	const lastPage = datos?.lastPage || 1;
	const pages = useMemo(() => buildPages(page, lastPage, (n) => { if (n >= 1 && n <= lastPage) setPage(n); }, movil), [page, lastPage, movil]);
	if (campusId === null) return <React.Fragment />;

	const filas = datos && datos.tab === tab ? datos.rows : null;
	const enPagina = filas ? filas.length : 0;
	const desde = (page - 1) * perPage + 1;
	const itemCount = { start: enPagina ? desde : 0, end: enPagina ? desde + enPagina - 1 : 0, total: datos?.total || 0 };
	const tenue = { opacity: loading ? 0.6 : 1 };
	const gris: React.CSSProperties = { fontSize: 13, color: "var(--wa-apagado)" };
	const elegirTab = (k: WorkshopTab) => { tocoPestana.current = true; setTab(k); setPage(1); };

	const nombre = (w: WorkshopRowMultiDTO) => {
		const cursos = (w.courses || []).map((c) => cursoCorto(c.name) || c.name).join(", ") || cursoCorto(w.courseName) || w.area || "";
		const sub = w.isPersonalizado && w.studentName ? w.name : cursos;
		return <div style={{ minWidth: 190 }}><b>{w.isPersonalizado && w.studentName ? w.studentName : w.name}</b>{sub ? <div style={gris}>{sub}</div> : null}</div>;
	};
	const dias = (w: WorkshopRowMultiDTO) => {
		const n = (w.sessionDates || []).length;
		const horas = w.sameTime === false ? S.segunDia : rangoHoras(w.timeFrom, w.timeTo);
		return <>{diasTexto(w.sessionDates || [])}<div style={gris}>{[n > 1 ? S.nDias(n) : "", horas].filter(Boolean).join(", ")}</div></>;
	};
	const maestra = (w: WorkshopRowMultiDTO) => w.sessionMentors || w.mentorName || "—";
	const inscritas = (w: WorkshopRowMultiDTO) => <span className={w.spotsLeft === 0 ? "wtlOro" : ""}>{w.attendees.total}{w.capacity ? " / " + w.capacity : ""}</span>;
	const cobrado = (w: WorkshopRowMultiDTO) => <>{monto(w.currency, w.collected || 0)}{(w.pending || 0) > 0 ? <div className="wtlOro" style={{ fontSize: 13 }}>{S.falta(monto(w.currency, w.pending))}</div> : null}</>;

	const lista = () => {
		if (!filas) return <div className="loader" />;
		if (movil) {
			if (!filas.length) return <div style={{ ...gris, textAlign: "center", padding: 24, fontSize: 15 }}>{S.vacio}</div>;
			return (
				<div style={{ display: "flex", flexDirection: "column", gap: 8, paddingBottom: 8 }}>
					{filas.map((w) => (
						<div key={w.id} className="wtlTarjeta" style={tenue} role="button" tabIndex={0} onClick={() => setAbierto(w.id)} onKeyDown={(e) => { if (e.key === "Enter") setAbierto(w.id); }}>
							<div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>{nombre(w)}<div className="wtlNum" style={{ fontSize: 14 }}>{cobrado(w)}</div></div>
							<div style={{ fontSize: 14, marginTop: 4 }}>{dias(w)}</div>
							<div style={{ display: "flex", justifyContent: "space-between", gap: 8, marginTop: 4, alignItems: "center" }}><span style={gris}>{maestra(w)}</span><Chip>{S.col.inscritas} {w.attendees.total}{w.capacity ? " / " + w.capacity : ""}</Chip></div>
						</div>
					))}
				</div>
			);
		}
		return (
			<table className="table table-hover wtlLista" style={{ fontSize: 15, marginBottom: 0 }}>
				<thead>
					<tr><th>{S.col.taller}</th><th style={{ width: 220 }}>{S.col.dias}</th><th style={{ width: 170 }}>{S.col.maestra}</th>
						<th className="wtlNum" style={{ width: 90 }}>{S.col.inscritas}</th><th className="wtlNum" style={{ width: 140 }}>{S.col.cobrado}</th></tr>
				</thead>
				<tbody>
					{!filas.length ? <tr><td colSpan={5} style={{ ...gris, textAlign: "center", padding: 24, fontSize: 15 }}>{S.vacio}</td></tr> : filas.map((w) => (
						<tr key={w.id} style={{ cursor: "pointer", ...tenue }} onClick={() => setAbierto(w.id)}>
							<td>{nombre(w)}</td>
							<td>{dias(w)}</td>
							<td style={{ fontSize: 14 }}>{maestra(w)}</td>
							<td className="wtlNum">{inscritas(w)}</td>
							<td className="wtlNum">{cobrado(w)}</td>
						</tr>
					))}
				</tbody>
			</table>
		);
	};

	return (
		<div className="container-fluid waModulo" style={{ textAlign: "left", padding: movil ? "10px 12px 0 12px" : "10px 24px 0 24px" }}>
			<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10, marginBottom: 8 }}>
				<h4 style={{ margin: 0 }}>{S.title}</h4>
				<button type="button" className="btn btn-primary waBotonGrande" onClick={() => setNuevo(true)}><FontAwesomeIcon icon={solid("plus")} />&nbsp;{S.nuevo}</button>
			</div>
			{error ? <div className="alert alert-danger" style={{ fontSize: 15 }}>{error}</div> : null}
			{aviso ? <div className="wtlAviso" role="status" style={{ marginBottom: 10 }}>{aviso}<button type="button" className="btn-close" style={{ fontSize: 10 }} aria-label={S.cerrar} onClick={() => setAviso(null)} /></div> : null}

			<Card>
				<div style={{ padding: movil ? "12px 12px 0 12px" : "14px 18px 4px 18px" }}>
					<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
						<ul className="nav nav-pills waTabs" style={{ gap: 4 }}>
							{S.tabs.map((x) => (
								<li className="nav-item" key={x.key}>
									<button type="button" className={"nav-link btn btn-sm " + (tab === x.key ? "active" : "")} style={{ padding: "4px 12px", fontSize: 15 }} onClick={() => elegirTab(x.key)}>
										{x.label}{CON_NUMERO.includes(x.key) && counts && counts[x.key] ? <span className="waTabCuenta">{counts[x.key]}</span> : null}
									</button>
								</li>
							))}
						</ul>
						<div style={movil ? { flex: "1 1 100%", position: "relative" } : { minWidth: 320, position: "relative" }}>
							<SearchInputComponent search={search} onChangeSearch={setSearch} onClickSearch={() => { setBuscar(search.trim()); setPage(1); }}
								onClearSearch={() => { setSearch(""); setBuscar(""); setPage(1); }} />
						</div>
					</div>
					<div className="waScrollX" style={{ overflowX: "auto", marginTop: 14 }}>{lista()}</div>
				</div>
			</Card>
			<div className="waSobreDock" style={{ marginTop: 10 }}>
				<PaginationComponent pages={pages} pageSizes={PAGE_SIZES} itemCount={itemCount} onPageSizeChange={(n: number) => { setPerPage(n); setPage(1); }} />
			</div>

			{abierto !== null ? <TallerVentana campusId={campusId} id={abierto} opciones={opciones} onClose={() => setAbierto(null)}
				onChanged={() => setRefresco((x) => x + 1)} notify={(t) => setAviso(t)} /> : null}
			{nuevo ? <WorkshopForm campusId={campusId} opciones={opciones} taller={null} onClose={() => setNuevo(false)}
				onSaved={(t, texto) => { setNuevo(false); setAviso(texto); setRefresco((x) => x + 1); setAbierto(t.id); }} /> : null}
		</div>
	);
};

export default WorkshopsController;
